import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Button, Card, Field, NumberInput, PageHead, Spinner, Switch, TextInput, useToast } from "../../components/ui";
import { LuxInput } from "../../components/LuxInput";
import { useT } from "../../i18n/I18nProvider";
import { LeaveGuard } from "../../lib/leaveGuard";

type Settings = S["PlatformSettings"];
type SettingsWrite = S["PlatformSettingsWrite"];

/** The PUT body: every value this page edits. Stage and pipeline switches are read-only here; they are written
 * through /v1/admin/stages and /v1/admin/pipeline-switches (the Stages page), which validate and audit them. */
export function settingsWriteBody(draft: Settings): SettingsWrite {
  const { disabled_stages: _stages, pipeline_switches: _switches, ...writable } = draft;
  // Compile-time guard: a field added to PlatformSettings but not to PlatformSettingsWrite would be rejected by the
  // API ("Extra inputs are not permitted"); it must be dropped above (or added to the write model) explicitly.
  const _onlyWritable: Record<Exclude<keyof typeof writable, keyof SettingsWrite>, never> = {};
  void _onlyWritable;
  return writable;
}

export function AdminPlatformSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const current = useQuery({ queryKey: ["platform-settings"], queryFn: () => unwrap(api.GET("/v1/admin/platform-settings")) });
  const [draft, setDraft] = useState<Settings | null>(null);
  const [newQueue, setNewQueue] = useState("");
  useEffect(() => {
    if (current.data) setDraft(structuredClone(current.data));
  }, [current.data]);
  const save = useMutation({
    mutationFn: (body: SettingsWrite) => unwrap(api.PUT("/v1/admin/platform-settings", { body })),
    onSuccess: (data) => {
      qc.setQueryData(["platform-settings"], data);
      qc.invalidateQueries({ queryKey: ["public-pricing"] });
      toast(t("admin.platformSettings.saved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (current.isLoading || !draft) return current.error ? <Alert tone="danger">{errorMessage(current.error)}</Alert> : <Spinner />;
  const set = (patch: Partial<Settings>) => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify(draft) !== JSON.stringify(current.data);
  const actions = (
    <div className="row">
      <Button variant="ghost" icon={<RotateCcw />} disabled={!dirty} onClick={() => setDraft(structuredClone(current.data!))}>
        {t("admin.platformSettings.discard")}
      </Button>
      <Button variant="primary" icon={<Save />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(settingsWriteBody(draft))}>
        {t("admin.platformSettings.save")}
      </Button>
    </div>
  );
  const int = (v: number | null) => Math.max(0, Math.round(v ?? 0));
  return (
    <>
      <LeaveGuard dirty={dirty} />
      <PageHead title={t("admin.platformSettings.title")} description={t("admin.platformSettings.description")} actions={actions} />
      <div className="split">
        <div className="stack">
          <Card title={t("admin.platformSettings.signup.title")} description={t("admin.platformSettings.signup.description")}>
            <div className="form-grid">
              <Field label={t("admin.platformSettings.signup.orgsPerIp")}>
                <NumberInput value={draft.signup_max_orgs_per_ip_per_day} min={0} step={1} onChange={(v) => set({ signup_max_orgs_per_ip_per_day: int(v) })} />
              </Field>
              <Field label={t("admin.platformSettings.signup.trialsPerIp")}>
                <NumberInput value={draft.trial_requests_max_per_ip_per_day} min={0} step={1} onChange={(v) => set({ trial_requests_max_per_ip_per_day: int(v) })} />
              </Field>
              <Field label={t("admin.platformSettings.signup.lowBalance")} hint={t("admin.platformSettings.signup.lowBalanceHint")}>
                <LuxInput value={draft.lux_low_threshold} onChange={(v) => v != null && set({ lux_low_threshold: v })} />
              </Field>
            </div>
          </Card>
          <Card title={t("admin.platformSettings.proposal.title")} description={t("admin.platformSettings.proposal.description")}>
            <div className="switch-row">
              <div>
                <b>{t("admin.platformSettings.proposal.deliver")}</b>
                <span className="hint">{t("admin.platformSettings.proposal.deliverHint")}</span>
              </div>
              <Switch checked={draft.proposal_delivery_enabled} onChange={(v) => set({ proposal_delivery_enabled: v })} />
            </div>
          </Card>
          <Card title={t("admin.platformSettings.planSets.title")} description={t("admin.platformSettings.planSets.description")}>
            <div className="switch-row">
              <div>
                <b>{t("admin.platformSettings.planSets.enabled")}</b>
                <span className="hint">{t("admin.platformSettings.planSets.enabledHint")}</span>
              </div>
              <Switch checked={draft.plan_sets_enabled} onChange={(v) => set({ plan_sets_enabled: v })} />
            </div>
            <div className="form-grid">
              <Field label={t("admin.platformSettings.planSets.price")} hint={t("admin.platformSettings.planSets.priceHint")}>
                <LuxInput value={draft.plan_set_lux} onChange={(v) => v != null && set({ plan_set_lux: v })} />
              </Field>
            </div>
          </Card>
          <Card title={t("admin.platformSettings.subscriptions.title")} description={t("admin.platformSettings.subscriptions.description")}>
            <div className="form-grid">
              <Field label={t("admin.platformSettings.subscriptions.grace")} hint={t("admin.platformSettings.subscriptions.graceHint")}>
                <NumberInput value={draft.billing_grace_days} min={0} max={90} step={1} suffix={t("admin.platformSettings.unitDays", { count: draft.billing_grace_days })} onChange={(v) => set({ billing_grace_days: int(v) })} />
              </Field>
            </div>
          </Card>
          <Card title={t("admin.platformSettings.prices.title")} description={t("admin.platformSettings.prices.description")}>
            <div className="form-grid">
              <Field label={t("admin.platformSettings.prices.limit")} hint={t("admin.platformSettings.prices.limitHint")}>
                <NumberInput value={draft.utility_price_limit} min={0} max={10000} step={1} suffix={t("admin.platformSettings.unitUtilities", { count: draft.utility_price_limit })} onChange={(v) => set({ utility_price_limit: int(v) })} />
              </Field>
            </div>
          </Card>
          <Card title={t("admin.platformSettings.migration.title")} description={t("admin.platformSettings.migration.description")}>
            <div className="form-grid">
              <Field label={t("admin.platformSettings.migration.minProjects")}>
                <NumberInput value={draft.migration_reward_min_projects} min={0} step={1} suffix={t("admin.platformSettings.unitProjects", { count: draft.migration_reward_min_projects })} onChange={(v) => set({ migration_reward_min_projects: int(v) })} />
              </Field>
              <Field label={t("admin.platformSettings.migration.minSpan")}>
                <NumberInput value={draft.migration_reward_min_span_days} min={0} step={1} suffix={t("admin.platformSettings.unitDays", { count: draft.migration_reward_min_span_days })} onChange={(v) => set({ migration_reward_min_span_days: int(v) })} />
              </Field>
              <Field label={t("admin.platformSettings.migration.minAge")}>
                <NumberInput value={draft.migration_reward_min_age_days} min={0} step={1} suffix={t("admin.platformSettings.unitDaysOld", { count: draft.migration_reward_min_age_days })} onChange={(v) => set({ migration_reward_min_age_days: int(v) })} />
              </Field>
              <Field label={t("admin.platformSettings.migration.minPriced")}>
                <NumberInput value={draft.migration_reward_min_priced} min={0} step={1} suffix={t("admin.platformSettings.unitProjects", { count: draft.migration_reward_min_priced })} onChange={(v) => set({ migration_reward_min_priced: int(v) })} />
              </Field>
            </div>
          </Card>
          <Card title={t("admin.platformSettings.firstPurchase.title")} description={t("admin.platformSettings.firstPurchase.description")}>
            <div className="form-grid">
              <Field label={t("admin.platformSettings.firstPurchase.discount")}>
                <NumberInput value={draft.first_purchase_discount_pct} min={0} max={100} step={1} suffix="%" onChange={(v) => set({ first_purchase_discount_pct: Math.min(100, int(v)) })} />
              </Field>
              <Field label={t("admin.platformSettings.firstPurchase.max")} hint={t("admin.platformSettings.firstPurchase.maxHint")}>
                <NumberInput value={draft.first_purchase_discount_max_lux} min={0} step={1} suffix="Lux" onChange={(v) => set({ first_purchase_discount_max_lux: int(v) })} />
              </Field>
            </div>
          </Card>
        </div>
        <Card title={t("admin.platformSettings.queues.title")} description={t("admin.platformSettings.queues.description", { setting: "\u2066JOB_MAX_RUNNING_<QUEUE>\u2069" })}>
          <div className="stack">
            {Object.entries(draft.max_running).length === 0 && <p className="muted">{t("admin.platformSettings.queues.empty")}</p>}
            {Object.entries(draft.max_running).map(([queue, cap]) => (
              <div key={queue} className="row" style={{ flexWrap: "nowrap" }}>
                <code style={{ flex: 1 }} dir="ltr">{queue}</code>
                <div style={{ inlineSize: 140 }}>
                  <NumberInput value={cap} min={0} step={1} suffix={t("admin.platformSettings.unitTasks", { count: cap })} onChange={(v) => set({ max_running: { ...draft.max_running, [queue]: int(v) } })} />
                </div>
                <Button
                  variant="ghost"
                  icon={<Trash2 />}
                  aria-label={t("admin.platformSettings.queues.remove", { queue })}
                  onClick={() => {
                    const next = { ...draft.max_running };
                    delete next[queue];
                    set({ max_running: next });
                  }}
                />
              </div>
            ))}
            <div className="input-group">
              <TextInput value={newQueue} onChange={(e) => setNewQueue(e.target.value.trim())} placeholder={t("admin.platformSettings.queues.placeholder")} aria-label={t("admin.platformSettings.queues.newQueue")} />
              <Button
                icon={<Plus />}
                disabled={!newQueue || newQueue in draft.max_running}
                onClick={() => {
                  set({ max_running: { ...draft.max_running, [newQueue]: 4 } });
                  setNewQueue("");
                }}
              >
                {t("admin.platformSettings.queues.add")}
              </Button>
            </div>
            <p className="hint">{t("admin.platformSettings.queues.perOrg")}</p>
          </div>
        </Card>
      </div>
    </>
  );
}
